import React, { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

const empty = { name: "", email: "", phone: "", company: "", interest: "Request a Demo", message: "" };

export default function EnquiryForm({ defaultInterest }) {
  const [form, setForm] = useState({ ...empty, interest: defaultInterest || empty.interest });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Thank you! Our team will contact you shortly.", { id: "enquiry" });
      setForm({ ...empty, interest: defaultInterest || empty.interest });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const cls = "border-slate-200 bg-white focus-visible:ring-brand";

  return (
    <form onSubmit={submit} className="rounded-2xl border border-slate-100 bg-white p-8 shadow-card" data-testid="enquiry-form">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>Full name *</Label>
          <Input value={form.name} onChange={set("name")} placeholder="Your name" className={cls} data-testid="input-name" />
        </div>
        <div className="space-y-2">
          <Label>Phone *</Label>
          <Input value={form.phone} onChange={set("phone")} placeholder="+91 ..." className={cls} data-testid="input-phone" />
        </div>
        <div className="space-y-2">
          <Label>Email *</Label>
          <Input type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className={cls} data-testid="input-email" />
        </div>
        <div className="space-y-2">
          <Label>Company</Label>
          <Input value={form.company} onChange={set("company")} placeholder="Business name" className={cls} data-testid="input-company" />
        </div>
      </div>
      <div className="mt-5 space-y-2">
        <Label>I'm interested in</Label>
        <Select value={form.interest} onValueChange={(v) => setForm((f) => ({ ...f, interest: v }))}>
          <SelectTrigger className={cls} data-testid="select-interest"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Request a Demo">Request a Demo</SelectItem>
            <SelectItem value="Buy a Machine">Buy a Machine</SelectItem>
            <SelectItem value="Fleet / Bulk Deployment">Fleet / Bulk Deployment</SelectItem>
            <SelectItem value="Become a Distributor">Become a Distributor</SelectItem>
            <SelectItem value="General Enquiry">General Enquiry</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="mt-5 space-y-2">
        <Label>Message</Label>
        <Textarea value={form.message} onChange={set("message")} placeholder="Tell us about your requirement..." rows={4} className={cls} data-testid="input-message" />
      </div>
      <button type="submit" disabled={loading} className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-orange px-7 py-3.5 font-display font-700 text-white transition-colors duration-200 hover:bg-brand-orange-dark disabled:opacity-70" data-testid="submit-enquiry-btn">
        {loading ? <Loader2 size={18} className="animate-spin" /> : <>Send Enquiry <ArrowRight size={18} /></>}
      </button>
    </form>
  );
}
